import { useNavigate } from '@tanstack/react-router';
import { useEffect, type ReactNode } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import styles from './AppShell.module.css';
import { Building2, LayoutDashboard, LogOut, Activity } from 'lucide-react';


interface NavItem { label: string; icon: ReactNode; to: string; }

const navItems: NavItem[] = [
  { label: 'Dashboard',     icon: <LayoutDashboard size={16} />, to: '/dashboard' },
  { label: 'Tenants',       icon: <Building2 size={16} />,       to: '/tenants' },
  { label: 'Observability', icon: <Activity size={16} />,        to: '/observability' },
];


export function AppShell({ children }: { children: ReactNode }) {
  const { firebaseUser, partnerUser, loading, signOut, error } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !firebaseUser) navigate({ to: '/login' });
  }, [loading, firebaseUser, navigate]);

  if (loading) return (
    <div className={styles.loader}>
      <div className={styles.spinner} />
    </div>
  );

  if (error === 'superadmin' || error === 'tenant') return (
    <div className={styles.redirect}>
      <div className={styles.redirectCard}>
        <div className={styles.redirectIcon}>↗</div>
        <h2>{error === 'superadmin' ? 'Redirecting to Admin Portal…' : 'Redirecting to App Portal…'}</h2>
        <p>{error === 'superadmin' ? 'Your account has SuperAdmin access.' : 'Your account is a tenant user.'}</p>
      </div>
    </div>
  );

  if (!firebaseUser) return null;

  // Wait for partner user resolution — don't render children that depend on partnerUser
  if (!partnerUser) return (
    <div className={styles.loader}>
      <div className={styles.spinner} />
    </div>
  );

  const pathname = window.location.pathname;

  return (
    <div className={styles.shell}>
      {/* Sidebar */}
      <aside className={styles.sidebar}>
        <div className={styles.logo}>
          <span className={styles.logoMark}>P</span>
          <span className={styles.logoText}>Partner</span>
        </div>

        {partnerUser && (
          <div className={styles.partnerBadge}>
            <span className={styles.partnerIcon}>🏢</span>
            <div>
              <div className={styles.partnerName}>{partnerUser.partner?.name}</div>
              <div className={styles.partnerRole}>{partnerUser.role} · {partnerUser.email}</div>
            </div>
          </div>
        )}

        <nav className={styles.nav}>
          {navItems.map((item) => (
            <a
              key={item.to}
              href={item.to}
              className={`${styles.navItem} ${pathname === item.to || pathname.startsWith(item.to + '/') ? styles.navItemActive : ''}`}
              onClick={(e) => { e.preventDefault(); navigate({ to: item.to as '/' }); }}
            >
              {item.icon}
              {item.label}
            </a>
          ))}
        </nav>

        <button className={styles.signOut} onClick={signOut}>
          <LogOut size={14} />
          Sign out
        </button>
      </aside>

      {/* Main content */}
      <main className={styles.main}>
        {children}
      </main>
    </div>
  );
}
